/**
 * The one place that knows which languages exist.
 *
 * UI languages are what the office portal speaks and this app renders;
 * `uz` is Latin-script Uzbek, `uz-Cyrl` its Cyrillic sibling (a generated
 * locale — see scripts/gen-uz-cyrl.mjs). Template languages are the content
 * tags a template family's members carry; a family rarely has all four, so
 * picking one walks a short chain and ends at the primary — the same rule
 * the backend applies (services/template_languages.py).
 *
 * Kept out of src/i18n.js on purpose: that module has import-time side
 * effects (it reads ?lang= into localStorage), which makes it awkward to
 * import from tests and from pure helpers.
 */

export const UI_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ru', label: 'Русский', short: 'RU' },
  { code: 'uz', label: 'Oʻzbekcha', short: 'UZ' },
  { code: 'uz-Cyrl', label: 'Ўзбекча (Кирилл)', short: 'ЎЗ' },
];
export const UI_LANGUAGE_CODES = UI_LANGUAGES.map((l) => l.code);
export const UI_LANGUAGE_SHORT = Object.fromEntries(UI_LANGUAGES.map((l) => [l.code, l.short]));

export const TEMPLATE_LANGUAGES = ['uz-Cyrl', 'uz-Latn', 'ru', 'en'];
export const TEMPLATE_LANGUAGE_LABELS = {
  'uz-Cyrl': 'Ўзбекча',
  'uz-Latn': 'Oʻzbekcha',
  ru: 'Русский',
  en: 'English',
};
export const TEMPLATE_LANGUAGE_SHORT = { 'uz-Cyrl': 'ЎЗ', 'uz-Latn': 'UZ', ru: 'RU', en: 'EN' };

const UI_TO_TEMPLATE = { uz: 'uz-Latn', 'uz-Latn': 'uz-Latn', 'uz-Cyrl': 'uz-Cyrl', ru: 'ru', en: 'en' };
const FALLBACK = {
  'uz-Cyrl': ['uz-Cyrl', 'uz-Latn'],
  'uz-Latn': ['uz-Latn', 'uz-Cyrl'],
  ru: ['ru'],
  en: ['en'],
};

/** A template tag for a UI (or template) code; null for anything unknown. */
export function uiToTemplateLanguage(code) {
  return UI_TO_TEMPLATE[code] || null;
}

/** The family member that best serves `uiLang`: exact, script sibling, else the primary. */
export function resolveVariant(tpl, uiLang) {
  const variants = Array.isArray(tpl?.variants) ? tpl.variants : [];
  if (!variants.length) return null;
  const tag = uiToTemplateLanguage(uiLang);
  for (const wanted of tag ? FALLBACK[tag] || [tag] : []) {
    const hit = variants.find((v) => v.language === wanted);
    if (hit) return hit;
  }
  return variants[0];
}

/** The template language a caller should send for the current UI language. */
export function languageForUi(tpl, uiLang) {
  const variant = resolveVariant(tpl, uiLang);
  return variant?.language || uiToTemplateLanguage(uiLang) || null;
}

/** The name to show for a family: the member matching the UI language. */
export function displayName(tpl, uiLang) {
  return resolveVariant(tpl, uiLang)?.name || tpl?.name || '';
}

/** Variants in the fixed language order (unknown tags last, by id). */
export function orderedVariants(tpl) {
  const variants = Array.isArray(tpl?.variants) ? [...tpl.variants] : [];
  const pos = (v) => {
    const i = TEMPLATE_LANGUAGES.indexOf(v.language);
    return i === -1 ? TEMPLATE_LANGUAGES.length : i;
  };
  return variants.sort((a, b) => pos(a) - pos(b) || a.id - b.id);
}
